import { FC, useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import Divider from '@material-ui/core/Divider';
import LockIcon from '@material-ui/icons/Lock';
import Loading from 'components/loading/Loading';
import useAuth from 'helpers/useAuth';
import useQueryParams from 'helpers/useQueryParams';
import mixpanel from 'mixpanel-browser';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { RegisterModel } from 'shared/auth/model/register.model';
import appRoutes from 'utils/routes';

import { todayDate } from '../../../../utils/dates';
import {
    InputsContainer,
    StyledAlert,
    StyledButton,
    StyledForm,
} from '../../Authentication.styles';

import {
    LoginLinkContainer,
    StyledEmailTextField,
    StyledInputAdornment,
    StyledLink,
} from './Register.styles';
import { schema } from './schema';

const Register: FC = () => {
    const { t } = useTranslation('authentication');
    const history = useHistory();
    const query = useQueryParams();

    const [isLoading, setIsLoading] = useState(false);
    const [emailParam, setEmailParam] = useState('');
    const [hasError, setHasError] = useState<any>({});

    const { show } = useIntercom();

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<RegisterModel>({
        defaultValues: {
            email: '',
        },
        resolver: yupResolver(schema),
        reValidateMode: 'onChange',
        mode: 'onChange',
    });
    const {
        actions: { register },
    } = useAuth();

    useEffect(() => {
        const email = query.get('emailAddress');

        if (email) {
            setEmailParam(email);
            setValue('email', email);
        }
    }, [query, setValue]);

    const onSubmit = useCallback(
        async (data: RegisterModel) => {
            setIsLoading(true);
            setHasError({});

            const result = await register(data);
            setIsLoading(false);
            if (result === true) {
                mixpanel.identify(emailParam);
                mixpanel.track('Sign up', {
                    email: emailParam,
                    created: todayDate(),
                });
                history.push({
                    pathname: appRoutes.login.root,
                    search: `?${new URLSearchParams({
                        emailAddress: emailParam,
                    }).toString()}`,
                });
            } else {
                if (result === 'user_exists') {
                    setHasError({ existingAccount: true });
                } else {
                    setHasError({ general: true });
                }
            }
        },
        [emailParam, register, history],
    );

    return (
        <div>
            {hasError.existingAccount && (
                <StyledAlert severity='error'>
                    {t('register.form.error.already-exists.start')}
                    <Link to={appRoutes.login.root}>
                        {t('register.form.error.already-exists.end')}
                    </Link>
                </StyledAlert>
            )}

            {hasError.general && (
                <StyledAlert severity='error'>
                    Sorry you’re experiencing difficulties logging in, please
                    contact us at support@selinafinance.co.uk or{' '}
                    <span
                        style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                        }}
                        onClick={() => show()}
                    >
                        use the live chat.
                    </span>
                </StyledAlert>
            )}

            <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate>
                <InputsContainer>
                    <Controller
                        control={control}
                        name='email'
                        render={({
                            field: { onChange, onBlur, value, ref },
                        }) => (
                            <StyledEmailTextField
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                inputRef={ref}
                                placeholder={t('register.form.email')}
                                type='email'
                                error={!!errors.email}
                                InputProps={{
                                    startAdornment: (
                                        <>
                                            {!emailParam && (
                                                <StyledInputAdornment position='start'>
                                                    <LockIcon fontSize='small' />
                                                </StyledInputAdornment>
                                            )}
                                        </>
                                    ),
                                }}
                                helperText={
                                    errors?.email?.type === 'email' &&
                                    errors?.email?.message
                                }
                                fullWidth
                                disabled
                                noSpacing={!emailParam}
                            />
                        )}
                    />
                </InputsContainer>
                <Divider />
                <div>
                    <Trans i18nKey='register.form.marketing.label'>
                        By pressing 'Get Started', you verify that you have read
                        agree with Selina Finance's{' '}
                        <StyledLink
                            target='blank'
                            to={{
                                pathname:
                                    'https://www.selinafinance.co.uk/terms-of-use',
                            }}
                        >
                            Terms and Conditions
                        </StyledLink>{' '}
                        and{' '}
                        <StyledLink
                            target='blank'
                            to={{
                                pathname:
                                    'https://www.selinafinance.co.uk/privacy-policy',
                            }}
                        >
                            Privacy Policy
                        </StyledLink>
                        .
                    </Trans>
                </div>
                <StyledButton
                    variant='contained'
                    color='primary'
                    type='submit'
                    fullWidth
                >
                    {isLoading ? <Loading /> : t('register.form.sign-up')}
                </StyledButton>
            </StyledForm>

            <LoginLinkContainer>
                <span>{t('register.sub-title')}</span>{' '}
                <StyledLink to={appRoutes.login.root}>
                    {t('register.sign-in')}
                </StyledLink>
            </LoginLinkContainer>
        </div>
    );
};

export default Register;
